import * as amplitude from "@amplitude/analytics-react-native"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { IS_WEB } from "@common/utils/mobileUtils"
import { authService } from "@data/services/authService"
import Constants from "expo-constants"
import * as Device from "expo-device"
import * as Notifications from "expo-notifications"
import React from "react"
import { Platform } from "react-native"
import { connect, useDispatch, useSelector } from "react-redux"
import { authConstants } from "../../data/constants/authConstants"
import FilledButtonM from "../components/button/FilledButtonM"
import TextM from "../components/text/TextM"
import { useApiStatus } from "../hooks/useApiStatus"
import { theme } from "../theme/theme"

export const useLogout = (props) => {
    const dispatch = useDispatch()
    const loggingOutRef = React.useRef(false)

    // Lu ici plutôt que confié à l'appelant : sans lui, la déconnexion laisse l'appareil inscrit
    // aux notifications du compte quitté. `props.expoToken` reste un repli.
    const expoToken =
        useSelector((state) => state.data.session?.object?.expo_token) ||
        props?.expoToken

    // Sous impersonation, se déconnecter, c'est sortir de la session impersonnée — le chemin du
    // bandeau (« Déconnecter ») : le jeton est révoqué et l'admin retrouve ses comptes. Une
    // déconnexion ordinaire laisserait le jeton vivant et mêlerait les deux jeux de comptes.
    const impersonating = useSelector(
        (state) => !!state.data.currentUser?.object?.impersonation
    )
    const exitingImpersonation = useSelector(
        (state) => !!state.api?.exitImpersonation?.request
    )

    const request =
        props?.logoutRequest ||
        props?.unsubscribeFromNotificationsRequest ||
        props?.getFamilyTokensRequest ||
        exitingImpersonation

    function performActualLogout() {
        loggingOutRef.current = false
        amplitude.reset()
        if (Platform.OS !== "web") {
            Notifications.dismissAllNotificationsAsync().then(() => {
                if (expoToken) {
                    props.apiRequest(
                        authService.unsubscribeFromNotifications,
                        {},
                        { expo_token: expoToken }
                    )
                }
            })
            dispatch({ type: authConstants.RESET_AUTH_STATE })
        }
        props.apiRequest(authService.logout, {}, {})
    }

    function onLogout() {
        if (impersonating) {
            props.apiRequest(authService.exitImpersonation)
            return
        }
        loggingOutRef.current = true
        // First call getFamilyTokens to refresh family data before logout
        props.apiRequest(
            authService.getFamilyTokens,
            {},
            { device_name: IS_WEB ? "WEB" : (Device.deviceName ?? "mobile") }
        )
    }

    // Listen for getFamilyTokens success to trigger actual logout
    // Guard with loggingOutRef so non-logout calls to getFamilyTokens don't trigger logout
    useApiStatus(
        authService.getFamilyTokens,
        null,
        true,
        () => {
            if (loggingOutRef.current) performActualLogout()
        },
        () => {
            if (loggingOutRef.current) performActualLogout()
        }
    )

    useApiStatus(authService.unsubscribeFromNotifications, null, true)

    return [onLogout, request]
}

const LogoutButton = (props) => {
    const { noVersion = false } = props

    const [onButtonPress, logoutRequest] = useLogout(props)

    const hasMultipleAccounts = props.storedTokens?.length > 1

    return (
        <>
            <FilledButtonM
                icon={"exchange-alt"}
                mode="outlined"
                style={{
                    marginTop: 20,
                }}
                color={theme.colors.error}
                onPress={onButtonPress}
                loading={logoutRequest || props.getUserRequest}
                disabled={logoutRequest || props.getUserRequest}
                label={"Changer de compte"}
            />

            {!noVersion && (
                <TextM
                    fontSize={12}
                    color={theme.colors.light}
                    style={{ textAlign: "center", marginBottom: 15 }}
                >
                    Version {Constants.expoConfig.version}
                </TextM>
            )}
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        getUserRequest: state.api.getUser?.request,
        successes: state.data.staticData?.successes,
        token: state.data.session?.object?.token,
        storedTokens: state.data.authTokens?.storedTokens,
        expoToken: state.data.session?.object?.expo_token || null,
        logoutRequest: state?.api?.logout?.request,
        unsubscribeFromNotificationsRequest:
            state?.api?.unsubscribeFromNotifications?.request,
        getFamilyTokensRequest: state?.api?.getFamilyTokens?.request,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(mapStateToProps, mapDispatchToProps)(LogoutButton)
